import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { type FeedbackStatus } from "@/types";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { canCreateFeedback } from "@/utils/planLimits";

type Board = {
  id: string;
  name: string;
  emoji: string;
};

type FeedbackFormData = {
  title: string;
  description: string;
  submitterName: string;
  submitterEmail: string;
  boardId: string;
};

// Add customSlug prop with default value of empty string
const NewFeedback = ({ customSlug = "" }: { customSlug?: string }) => {
  const { slug: urlSlug } = useParams();
  const slug = customSlug || urlSlug; // Use customSlug if provided, otherwise use URL param

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [boards, setBoards] = useState<Board[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();
  const form = useForm<FeedbackFormData>({
    defaultValues: {
      title: "",
      description: "",
      submitterName: "",
      submitterEmail: "",
      boardId: "",
    },
  });

  useEffect(() => {
    const fetchBoards = async () => {
      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("slug", slug)
        .single();

      if (company) {
        const { data: boards } = await supabase
          .from("feedback_boards")
          .select("id, name, emoji")
          .eq("company_id", company.id);

        if (boards) {
          setBoards(boards);
          // Set the first board as default
          if (boards.length > 0) {
            form.setValue("boardId", boards[0].id);
          }
        }
      }
    };

    fetchBoards();
  }, [slug, form]);

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);
    try {
      // First get the company id from the slug
      const { data: company, error: companyError } = await supabase
        .from("companies")
        .select("id, plan")
        .eq("slug", slug)
        .single();

      if (companyError) throw companyError;

      // Check feedback count against plan limits
      const { count, error: countError } = await supabase
        .from("feedback")
        .select("*", { count: 'exact', head: true })
        .eq("company_id", company.id);

      if (countError) throw countError;

      const currentCount = count || 0;
      const planCheck = canCreateFeedback(company.plan, currentCount);

      if (!planCheck.allowed) {
        toast({
          title: "Limit Reached",
          description: planCheck.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Get the default status (position 0) for this company
      const { data: defaultStatus, error: statusError } = await supabase
        .from("company_statuses")
        .select("id, name")
        .eq("company_id", company.id)
        .order("position", { ascending: true })
        .limit(1)
        .single();

      if (statusError) {
        console.error("Error fetching default status:", statusError);
        // If there's an error, we'll just let the database handle the default status
        console.log(
          "Proceeding without setting a status, database will use its default"
        );
      }

      // Get the current user (if logged in)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { error } = await supabase.from("feedback").insert({
        title: data.title,
        description: data.description,
        status: defaultStatus?.name || null,
        created_by: session?.user?.id || null,
        submitter_name: data.submitterName,
        submitter_email: data.submitterEmail,
        company_id: company.id,
        board_id: data.boardId,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feedback submitted successfully!",
      });

      // Check if we're on a custom domain
      const hostname = window.location.hostname;
      if (
        hostname === "feedback.feednatic.com" ||
        hostname.startsWith("feedback.")
      ) {
        navigate("/"); // Navigate to root on custom domains
      } else {
        navigate(`/${slug}`); // Navigate to /:slug otherwise
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-background">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Submit New Feedback</CardTitle>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="submitterName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your name" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="submitterEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="boardId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Board</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a board" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {boards.map((board) => (
                            <SelectItem key={board.id} value={board.id}>
                              <div className="flex items-center space-x-2">
                                <span>{board.emoji}</span>
                                <span>{board.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter feedback title" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe your feedback in detail"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/${slug}`)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Submit Feedback
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default NewFeedback;
